import { Node, ResolvedPos } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";
import { Editor } from "@tiptap/react";

import { getDynamicData } from "@/view/TiptapArticleBody/extensions/DataProvider";
import {
  ANNOTABLE_INLINE_NODE_TYPES,
  ANNOTATIONS_NODE_TYPE_MAPPING,
} from "@/view/TiptapArticleBody/extensions/TextHighlighting/Annotations";

interface IPathSegment {
  type: string | undefined;
  index: number;
}

export function getAnnotationsClickHandler(
  { state }: Editor,
  setDraftAnnotation: Function,
  focusAnnotationCard: Function,
) {
  return () => {
    const { $from: fromNodePosition, $to: toNodePosition } = state.selection;

    const start = getXpathByNodePosition(state, fromNodePosition.pos);
    const end = getXpathByNodePosition(state, toNodePosition.pos);

    // TODO: deal with this more gracefully
    if (!start || !end) return;

    const startoffset = fixStartOffsetForTextSelection(fromNodePosition);
    /**
     * powerWord_Block and keyTerm_Block add two extra characters to the text selection,
     * but this only affects the text displayed in the side panel—the highlighting
     * itself remains unchanged. We need to adjust for this discrepancy.
     * IMPORTANT: This applies to the endOff only.
     */
    const endoffset = fixEndOffsetForTextSelection(toNodePosition);

    const { levelId, legacyArticleId } = getDynamicData(state);

    const draftAnnotation = {
      article_id: legacyArticleId,
      category: "user",
      end,
      endoffset,
      id: 0,
      pen_name: "yellow",
      start,
      startoffset,
      article: {
        content_id: levelId,
      },
    };

    setDraftAnnotation(draftAnnotation);
    focusAnnotationCard("0");
  };
}

/**
 * Generates an XPath-like string representing the hierarchical position of a selected node
 * in the ProseMirror document, correctly adjusting for non-text nodes like `image_block`.
 *
 * This function traverses the ProseMirror document tree upwards, determining the correct index of each node
 * relative to its siblings of the same type. It ensures that nested structures are properly represented
 * in the XPath format and accounts for non-text elements that can affect positioning.
 *
 * @param {EditorState} editorState - The current state of the ProseMirror editor.
 * @param {number} nodePosition - The absolute position in the ProseMirror document where the node is selected.
 * @returns {string} - The dynamically generated XPath-like string representing the node's location.
 *
 * Example Output:
 * - A paragraph inside a blockquote: `/blockquote[1]/p[3]`
 * - A list item inside a blockquote: `/blockquote[1]/ul[1]/li[2]/p[1]`
 * - A deeply nested structure: `/blockquote[1]/ul[1]/li[2]/p[1]/span[1]`
 *
 * How it Works:
 * - Resolves the node at the given position in the document.
 * - Iterates upwards through the document tree, processing each node and determining its index among siblings of the same type.
 * - Uses a node type mapping to convert ProseMirror node names to their expected HTML-equivalent tags.
 * - Adjusts the index when encountering non-text nodes (e.g., `image_block`) to ensure accurate XPath representation.
 * - Constructs the XPath by prepending each node and its calculated index to the path array.
 * - Returns the final XPath as a string.
 *
 * Edge Cases Handled:
 * - If a node is preceded by an `image_block`, its index is incremented to reflect its actual position.
 * - If the selected node is at the root level, the function ensures the correct path format.
 * - Supports deeply nested structures with multiple node types.
 */
export function getXpathByNodePosition(
  editorState: EditorState,
  nodePosition: number,
): string {
  const { doc } = editorState;
  const path: string[] = [];
  const resolvedPos = doc.resolve(nodePosition);

  if (!resolvedPos.parent) return ""; // Ensure valid node

  function buildPath(depth: number, currentNode: Node | null): void {
    if (!currentNode || depth <= 0) return;

    const parent = resolvedPos.node(depth - 1);
    const mappedType = getNodeType(currentNode);

    let nodeIndex = 0;
    let found = false;
    let adjustment = 0; // Tracks index adjustment for `image_block`

    for (let i = 0; i < parent.childCount; i++) {
      const child = parent.child(i);

      // If the previous node was an image_block, adjust the index
      if (child.type.name === "image_block") {
        adjustment++;
        continue;
      }

      const isParagraph = child.type.name === "paragraph";
      const isEmpty = child.textContent.trim().length === 0;

      // Ensure empty paragraphs are ignored (same as addXpathToNodes)
      if (isParagraph && isEmpty) continue;

      if (child.type.name === currentNode.type.name) {
        nodeIndex++;
      }

      if (!found && child.eq(currentNode)) {
        found = true;
        // The image_block adjustment is only needed for paragraphs
        const index = isParagraph ? nodeIndex + adjustment : nodeIndex;
        path.unshift(`/${mappedType}[${index}]`);
      }
    }

    buildPath(depth - 1, parent);
  }

  buildPath(resolvedPos.depth, resolvedPos.parent);
  return path.join("");
}

/**
 * Finds the absolute ProseMirror document position for a given XPath-like string.
 *
 * This function traverses the ProseMirror document tree downward, resolving the correct position
 * based on the provided XPath. It ensures that nested structures are properly accounted for and
 * returns the exact ProseMirror index for the target node.
 *
 * @param {EditorState} editorState - The current state of the ProseMirror editor.
 * @param {string} xpath - The XPath-like string representing the node's location.
 * @param {boolean} isEnd - Whether the provided XPath refers to the end offset of not.
 * @returns {number | null} - The absolute ProseMirror position of the node, or null if not found.
 *
 * Example Input:
 * - XPath for a paragraph inside a blockquote: `/blockquote[1]/p[3]`
 * - XPath for a list item inside a blockquote: `/blockquote[1]/ul[1]/li[2]/p[1]`
 * - XPath for a deeply nested structure: `/blockquote[1]/ul[1]/li[2]/p[1]/span[1]`
 *
 * Example Output:
 * - Given `/blockquote[1]/p[1]`, returns the absolute document position of `p[1]` inside the first `blockquote`.
 * - Given `/blockquote[1]/p[3]`, returns the document position of `p[3]` inside the first `blockquote`.
 * - Given `/p[7]`, returns the absolute document position of the seventh root-level paragraph.
 *
 * How it Works:
 * - Parses the XPath string into an array of node type-index pairs.
 * - Iterates through the document tree, searching for the specified node type and index at each level.
 * - Uses a sibling counter to ensure the correct occurrence of each node type is selected.
 * - Accumulates the absolute ProseMirror position as it traverses deeper into the hierarchy.
 * - Adds an offset for nested nodes to ensure the cursor is placed inside the child, not at the parent.
 * - Returns the final absolute position of the target node.
 */

export function getNodePositionByXpath(
  editorState: EditorState,
  xpath: string,
  isEnd: boolean = false,
): number | undefined {
  const { doc } = editorState;
  let nodePosition: number | undefined;

  // Parse XPath into segments: /blockquote[1]/p[1] -> [{ type: 'blockquote', index: 0 }, { type: 'p', index: 0 }]
  const pathSegments: IPathSegment[] = [...xpath.matchAll(/\/(\w+)\[(\d+)\]/g)]
    .map((match) => ({
      type: match[1],
      index: parseInt(match[2] || "", 10) - 1, // Convert from 1-based index to 0-based
    }))
    .filter(({ type }) => type !== "span"); // ignoring key terms and power words

  if (pathSegments.length === 1 && pathSegments[0])
    nodePosition = resolveDirectChildren(doc, pathSegments[0], isEnd);
  else if (pathSegments.length > 1)
    nodePosition = resolveNestedChildren(doc, pathSegments, isEnd);

  return nodePosition;
}

export function resolveDirectChildren(
  doc: Node,
  { type, index }: IPathSegment,
  isEnd: boolean = false,
) {
  let currentNode: Node = doc;
  let absolutePos = 0;

  let count = -1;
  let foundNode = false;
  let adjustment = 0; // Tracks index adjustment for `image_block`

  for (
    let childOffset = 0;
    childOffset < currentNode.childCount;
    childOffset++
  ) {
    const child = currentNode.child(childOffset);
    const mappedType = getNodeType(child);

    const isParagraph = child.type.name === "paragraph";
    const isEmpty = child.textContent.trim().length === 0;

    if (isParagraph && isEmpty) continue;

    if (child.type.name === "image_block") {
      adjustment++; // Track the presence of `image_block`
    }

    if (mappedType === type) {
      const realIndex = isParagraph ? index - adjustment : index;

      count++;
      if (count === realIndex) {
        absolutePos += childOffset;
        currentNode = child;
        foundNode = true;
        break;
      }
    }
  }

  if (!foundNode) {
    console.error(`Node ${type}[${index + 1}] not found in document!`);
    return;
  }

  let foundPos: number | undefined;
  const targetNode = doc.child(absolutePos);

  doc.descendants((node, pos) => {
    if (node === targetNode) {
      foundPos = pos;
      return false; // Stop traversal
    }
    return true; // Continue traversal
  });

  /**
   * powerWord_Block and keyTerm_Block add two extra characters to the text selection,
   * but this only affects the text displayed in the side panel—the highlighting
   * itself remains unchanged. We need to adjust for this discrepancy.
   * IMPORTANT: This applies to the endOff only.
   */
  if (foundNode && foundPos !== undefined && isEnd) {
    return foundPos + fixEndOffsetForAnnotableInlineNodes(targetNode);
  }

  return foundPos;
}

export function resolveNestedChildren(
  doc: Node,
  pathSegments: IPathSegment[],
  isEnd: boolean = false,
) {
  let foundPos: number | undefined;
  let pathIndex = 0; // Keep track of how deep we are in the XPath
  let currentIndex = 0; // Track occurrences of each type in the hierarchy
  let foundNode: Node | undefined;

  doc.descendants((node, pos) => {
    if (foundPos !== undefined) return false; // Stop early if found

    const isParagraph = node.type.name === "paragraph";
    const isEmpty = !node.content || node.content.size === 0;
    if (isParagraph && isEmpty) return true; // Continue traversal

    const mappedType = getNodeType(node);
    const segment = pathSegments[pathIndex];

    if (mappedType === segment?.type) {
      if (currentIndex === segment?.index) {
        if (pathIndex === pathSegments.length - 1) {
          foundNode = node;
          foundPos = pos;
          return false; // Stop traversal
        } else {
          pathIndex++; // Move to the next XPath segment
          currentIndex = 0; // Reset index tracking for the next level
        }
      } else {
        currentIndex++; // Count matching nodes at this level
      }
    }
    return true; // Continue traversal
  });

  /**
   * powerWord_Block and keyTerm_Block add two extra characters to the text selection,
   * but this only affects the text displayed in the side panel—the highlighting
   * itself remains unchanged. We need to adjust for this discrepancy.
   * IMPORTANT: This applies to the endOff only.
   */
  if (foundNode && foundPos !== undefined && isEnd) {
    return foundPos + fixEndOffsetForAnnotableInlineNodes(foundNode);
  }

  return foundPos;
}

export function getNodeType(node: Node) {
  const name = node.type.name;
  const level = node.attrs?.["level"];
  const isHeading = name === "headings_block";

  return isHeading && level
    ? `h${level}`
    : ANNOTATIONS_NODE_TYPE_MAPPING[name] || name;
}

/**
 * Adjusts the end offset for a text selection to account for inline custom nodes
 * such as `powerWord_block` and `keyTerm_block`.
 *
 * It reduces the offset for each annotable inline node in the parent and then restores
 * it for those within the selection.
 *
 * The final offset is clamped to the maximum text content length of the parent node.
 */
export function fixEndOffsetForTextSelection(resolvedPosition: ResolvedPos) {
  let endOffset = resolvedPosition.parentOffset;

  const from = resolvedPosition.pos - resolvedPosition.parentOffset;
  const to = resolvedPosition.pos;

  let qtAnnotableInlineNode = 0;
  let qtAnnotableInlineNodeWithinSelection = 0;

  resolvedPosition.parent.descendants((node) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(node.type.name)) {
      endOffset = endOffset - 2;
      qtAnnotableInlineNode++;
    }
  });

  resolvedPosition.doc.nodesBetween(from, to, (child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name))
      qtAnnotableInlineNodeWithinSelection++;
  });

  endOffset +=
    (qtAnnotableInlineNode - qtAnnotableInlineNodeWithinSelection) * 2;

  const maxOffset = resolvedPosition.parent.textContent.length;
  return Math.min(endOffset, maxOffset);
}

export function fixStartOffsetForTextSelection(resolvedPosition: ResolvedPos) {
  let startOffset = resolvedPosition.parentOffset;

  const from = resolvedPosition.pos - resolvedPosition.parentOffset;
  const to = resolvedPosition.pos;

  let qtAnnotableInlineNodeWithinSelection = 0;

  resolvedPosition.doc.nodesBetween(from, to, (child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name))
      qtAnnotableInlineNodeWithinSelection++;
  });

  startOffset -= qtAnnotableInlineNodeWithinSelection * 2;

  return startOffset < 0 ? 0 : startOffset;
}

export function fixEndOffsetForAnnotableInlineNodes(parent: Node | null) {
  let fixEndOffset = 0;
  parent?.descendants((child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name)) {
      fixEndOffset = fixEndOffset + 2;
    }
  });
  return fixEndOffset;
}

export function fixEndOffsetForTextHighlighting(
  state: EditorState,
  startIndex: number,
  endPosition: number,
  originalEndIndex: number,
) {
  let endIndex = originalEndIndex;
  /* Fixing endIndex with line breaks =============== */
  let lineBreakCount = -1;
  state.doc.nodesBetween(startIndex, endIndex, (child) => {
    if (child.type.name === "paragraph") lineBreakCount += 1;
  });
  endIndex += lineBreakCount > 0 ? lineBreakCount * 2 : 0;
  /* ================================================ */

  /* Fixing endIndex with ANNOTABLE_INLINE_NODE_TYPES == */
  const resolved = state.doc.resolve(endIndex);
  const node = resolved.node(resolved.depth);
  let qtAnnotableInlineNode = 0;
  node.descendants((child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name)) {
      qtAnnotableInlineNode += 1;
    }
  });
  let qtAnnotableInlineNodeWithinSelection = 0;
  state.doc.nodesBetween(endPosition, endIndex, (child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name))
      qtAnnotableInlineNodeWithinSelection += 1;
  });
  endIndex -=
    (qtAnnotableInlineNode - qtAnnotableInlineNodeWithinSelection) * 2;
  /* =================================================== */

  return endIndex;
}

export function fixStartOffsetForTextHighlighting(
  state: EditorState,
  originalStartIndex: number,
  startPosition: number,
) {
  let startIndex = originalStartIndex;

  let qtAnnotableInlineNodeWithinSelection = 0;
  state.doc.nodesBetween(startPosition, startIndex, (child) => {
    if (ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name))
      qtAnnotableInlineNodeWithinSelection += 1;
  });
  startIndex += qtAnnotableInlineNodeWithinSelection * 2;

  return startIndex;
}
